import React, {useState, useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {
    Box,
    Button, Checkbox,
    Dialog,
    DialogContent,
    DialogTitle, FormControlLabel, Grid, Radio, RadioGroup,
    Typography
} from "@material-ui/core";
import {ErrorMessage, Field, Form, Formik, useFormikContext} from "formik";
import {TextField} from "formik-material-ui";
import * as Yup from "yup";
import {colorError} from "../../../Constants/theme";
import {prescriptionTypes, sideEffects} from "../../../Constants/prescriptionTypes";
import {useSelector} from "react-redux";
import {RootState} from "../../../redux/store";
import {IPrescription} from "../../../interfaces";


const useStyles = makeStyles({
    dialog: {
        padding: "20px"
    },
    formItem: {
        marginBottom: "1em",
    },
    button: {
        margin: "1.5em 1.5em 0 0",
        padding: "13px 28px",
    },
    typeBtn: {
        margin: "0.5em 0.5em 0 0.5em",
        padding: "13px 28px",
        width: 152,
    },
    deleteButton: {
        margin: "1.5em 1.5em 0 0",
        padding: "13px 28px",
        border: "1.5px solid #FF6D6D",
        color: "#EA5858"
    },
    formError: {
        color: colorError,
        fontSize: "12px"
    }
});
const Count = () => {
    const {values, submitForm, resetForm, setFieldValue}: any = useFormikContext();
    const {
        numberOfTabletsDaily: tablets,
        numberOfDaysToGiveTheMedication: days,
        howManyMl: ml,
        howManyTimesGivenPerDay: times,
    } = values;
    useEffect(() => {
        if (days && times && ml) {
            const calcQuantity = days * times * ml;
            setFieldValue("quantity", calcQuantity?.toString());
        }
        if (days && tablets) {
            const calcQuantity = days * tablets;
            setFieldValue("quantity", calcQuantity?.toString());
        }
    }, [values.numberOfTabletsDaily,
        values.numberOfDaysToGiveTheMedication,
        values.howManyMl,
        values.howManyTimesGivenPerDay,
        values.numberOfDaysToGiveTheMedication,
    ]);

    return null;
};

const ClearOnTypeChange = ({type}) => {
    const {resetForm}: any = useFormikContext();
    useEffect(() => {resetForm();}, [type]);
    return null;
};

// eslint-disable-next-line react/prop-types
const PrescriptionModal = ({isOpen, setOpenDialog, handleSubmit, itemToEdit, setItemToEdit, handleDelete, appointmentType}) => {
    const classes = useStyles();
    const [prescriptionType, setPrescriptionType] = useState("TABLET");
    const initialSideEffectsState = {
        VOMITING: false,
        DIARRHEA: false,
        DROWSINESS: false,
    };
    const [selectedSideEffects, setSelectedSideEffects] = useState(initialSideEffectsState);
    const initialValues = {
        name: "",
        strength: "",
        quantity: "",
        refills: "",
        labelInstructions: "",
        fillingOption: "",
        notes: "",
        comment: "",
        numberOfTabletsDaily: "",
        howManyMl: "",
        howManyTimesGivenPerDay: "",
        numberOfDaysToGiveTheMedication: "",
        giveWithFoodAndWater: "false",
    };
    const [formikValues, setFormikValues] = useState({...initialValues});
    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Required"),
        strength: Yup.string().required("Required"),
        quantity: Yup.string().required("Required"),
        refills: Yup.string().required("Required"),
        labelInstructions: Yup.string().required("Required"),
        fillingOption: Yup.string().required("Required"),
    });
    const appointment = useSelector((state: RootState) => state?.appointment.appointment);

    const handleClose = () => {
        if (itemToEdit) setItemToEdit(null);
        setOpenDialog(false);
    };

    useEffect(() => {
        setOpenDialog(isOpen);
    }, [isOpen]);

    useEffect(() => {
        if (itemToEdit) {
            setPrescriptionType(itemToEdit?.type || "OTHER");
            const newFormikValues = {
                name: itemToEdit.name || "",
                strength: itemToEdit.strength || "",
                quantity: itemToEdit.quantity || "",
                refills: itemToEdit.refills || "",
                labelInstructions: itemToEdit.labelInstructions || "",
                notes: itemToEdit.notes || "",
                fillingOption: itemToEdit?.fillingOption || "",
                numberOfTabletsDaily: itemToEdit?.numberOfTabletsDaily || "",
                numberOfDaysToGiveTheMedication: itemToEdit?.numberOfDaysToGiveTheMedication || "",
                howManyMl: itemToEdit?.howManyMl || "",
                howManyTimesGivenPerDay: itemToEdit?.howManyTimesGivenPerDay || "",
                giveWithFoodAndWater: itemToEdit?.giveWithFoodAndWater?.toString(),

            };
            setSelectedSideEffects(mapSideEffects(itemToEdit.potentialSideEffects));
            setFormikValues({...initialValues, ...newFormikValues});
        } else {
            setFormikValues({...initialValues});
        }
    }, [itemToEdit]);

    const handleSideEffectsChange = (event) => {
        const {name} = event.target;
        setSelectedSideEffects(prev => (
            {
                ...selectedSideEffects,
                [name]: !prev[name],
            })
        );
    };

    const submitSideEffects = () => (
        Object.entries(selectedSideEffects)
            .filter(([prop, val]) => val)
            .map(([prop, val]) => {
                if (val) return prop;
            })
    );

    const mapSideEffects = (arr) => {
        const res = {...initialSideEffectsState};
        for (const property in res) {
            res[property] = !!arr.includes(property);
        }
        return res;
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            classes={{paper: classes.dialog}}
            scroll="body"
            disableEnforceFocus
        >
            <DialogTitle color="primary">
                <Typography variant="h3">
                    {itemToEdit ? "Edit" : "New"} {appointmentType.includes("ADVICE") ? "OTC" : "Prescription / OTC"}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container justify="space-around" alignItems="center">
                    {
                        prescriptionTypes.map(({label, value}, idx) => (
                            <Grid item key={idx}>
                                <Button
                                    type="button"
                                    color={prescriptionType === value ? "primary" : "default"}
                                    variant="contained"
                                    classes={{root: classes.typeBtn}}
                                    onClick={() => {
                                        setPrescriptionType(value);
                                        setFormikValues({...initialValues});
                                    }}
                                >
                                    {label}
                                </Button>
                            </Grid>
                        ))
                    }
                </Grid>
                <Formik
                    initialValues={formikValues}
                    enableReinitialize
                    onSubmit={(values) => {
                        let prescriptionRequest: IPrescription = {
                            prescriptionType: prescriptionType,
                            name: values.name,
                            strength: values.strength,
                            quantity: values.quantity,
                            refills: values.refills,
                            labelInstructions: values.labelInstructions,
                            fillingOption: values.fillingOption,
                            giveWithFoodAndWater: values.giveWithFoodAndWater === "true",
                            potentialSideEffects: submitSideEffects(),
                            numberOfDaysToGiveTheMedication: values.numberOfDaysToGiveTheMedication,
                            notes: values.notes,
                        };
                        if (prescriptionType === "TABLET") {
                            prescriptionRequest = {
                                ...prescriptionRequest,
                                numberOfTabletsDaily: values.numberOfTabletsDaily,
                            };
                        } else if (prescriptionType === "LIQUID") {
                            prescriptionRequest = {
                                ...prescriptionRequest,
                                howManyMl: values.howManyMl,
                                howManyTimesGivenPerDay: values.howManyTimesGivenPerDay,
                            };
                        }
                        handleSubmit(prescriptionRequest);
                        setOpenDialog(false);
                    }}
                    validationSchema={validationSchema}
                >

                    {(props) => {
                        return (
                            <Form>
                                <Count />
                                <ClearOnTypeChange type={prescriptionType} />
                                <Grid container justify="flex-start" alignItems="center">

                                    <Grid item xs={12}>
                                        <Box marginBottom="15px" marginTop="15px">
                                            <Typography variant="h4">Name of medication</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} className={classes.formItem}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Required"
                                            name="name"
                                            id="name"
                                            aria-labelledby="medication name"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    color: "rgba(0, 0, 0, 0.5)",
                                                    fontStyle: "italic"
                                                }
                                            }}
                                            onChange={props.handleChange("name")}
                                            onBlur={props.handleBlur("name")}
                                            value={props.values.name}
                                        />
                                    </Grid>
                                    {prescriptionType === "TABLET"
                                    &&
                                    <>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Medication strength</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={10} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="strength"
                                                id="strength"
                                                aria-labelledby="strength"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("strength")}
                                                onBlur={props.handleBlur("strength")}
                                                value={props.values.strength}
                                            />
                                        </Grid>
                                        <Grid item xs={2} className={classes.formItem}>
                                            <Box display="flex">
                                                <Typography style={{margin: "auto"}} variant="h4">MG</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Number of tablets daily</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                id="numberOfTabletsDaily"
                                                aria-labelledby="Number of tablets daily"
                                                name="numberOfTabletsDaily"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("numberOfTabletsDaily")}
                                                onBlur={props.handleBlur("numberOfTabletsDaily")}
                                                value={props.values.numberOfTabletsDaily}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Number of days to give the
                                                    medication</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                id="numberOfDaysToGiveTheMedication"
                                                aria-labelledby="Number of days to give the medication"
                                                name="numberOfDaysToGiveTheMedication"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("numberOfDaysToGiveTheMedication")}
                                                onBlur={props.handleBlur("numberOfDaysToGiveTheMedication")}
                                                value={props.values.numberOfDaysToGiveTheMedication}
                                            />
                                        </Grid>
                                    </>
                                    }
                                    {prescriptionType === "LIQUID"
                                    &&
                                    <>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Medication concentration</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                id="strength"
                                                aria-labelledby="Medication concentration"
                                                name="strength"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("strength")}
                                                onBlur={props.handleBlur("strength")}
                                                value={props.values.strength}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">How many mL</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={10} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="howManyMl"
                                                id="howManyMl"
                                                aria-labelledby="How many mL"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("howManyMl")}
                                                onBlur={props.handleBlur("howManyMl")}
                                                value={props.values.howManyMl}
                                            />
                                        </Grid>
                                        <Grid item xs={2} className={classes.formItem}>
                                            <Box display="flex">
                                                <Typography style={{margin: "auto"}} variant="h4">mL</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">How many times given per day</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="howManyTimesGivenPerDay"
                                                id="howManyTimesGivenPerDay"
                                                aria-labelledby="How many times given per day"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("howManyTimesGivenPerDay")}
                                                onBlur={props.handleBlur("howManyTimesGivenPerDay")}
                                                value={props.values.howManyTimesGivenPerDay}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Number of days to give the
                                                    medication</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="numberOfDaysToGiveTheMedication"
                                                id="numberOfDaysToGiveTheMedication"
                                                aria-labelledby="Number of days to give the medication"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("numberOfDaysToGiveTheMedication")}
                                                onBlur={props.handleBlur("numberOfDaysToGiveTheMedication")}
                                                value={props.values.numberOfDaysToGiveTheMedication}
                                            />
                                        </Grid>
                                    </>
                                    }
                                    {prescriptionType === "OTHER"
                                    &&
                                    <>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Medication strength</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="strength"
                                                id="strength"
                                                aria-labelledby="Medication strength"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("strength")}
                                                onBlur={props.handleBlur("strength")}
                                                value={props.values.strength}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box marginBottom="15px">
                                                <Typography variant="h4">Number of days to give the
                                                    medication</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} className={classes.formItem}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Required"
                                                name="numberOfDaysToGiveTheMedication"
                                                id="numberOfDaysToGiveTheMedication"
                                                aria-labelledby="Number of days to give the medication"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        color: "rgba(0, 0, 0, 0.5)",
                                                        fontStyle: "italic"
                                                    }
                                                }}
                                                onChange={props.handleChange("numberOfDaysToGiveTheMedication")}
                                                onBlur={props.handleBlur("numberOfDaysToGiveTheMedication")}
                                                value={props.values.numberOfDaysToGiveTheMedication}
                                            />
                                        </Grid>
                                    </>
                                    }
                                    <Grid item xs={12}>
                                        <Box marginBottom="15px">
                                            <Typography variant="h4">Quantity</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} className={classes.formItem}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Required"
                                            name="quantity"
                                            id="quantity"
                                            aria-labelledby="quantity"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    color: "rgba(0, 0, 0, 0.5)",
                                                    fontStyle: "italic"
                                                }
                                            }}
                                            onChange={props.handleChange("quantity")}
                                            onBlur={props.handleBlur("quantity")}
                                            value={props.values.quantity}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box marginBottom="15px">
                                            <Typography variant="h4">Refills</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} className={classes.formItem}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Required"
                                            name="refills"
                                            id="refills"
                                            aria-labelledby="refills"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    color: "rgba(0, 0, 0, 0.5)",
                                                    fontStyle: "italic"
                                                }
                                            }}
                                            onChange={props.handleChange("refills")}
                                            onBlur={props.handleBlur("refills")}
                                            value={props.values.refills}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box marginBottom="15px">
                                            <Typography variant="h4">Label instructions</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} className={classes.formItem}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Required"
                                            name="labelInstructions"
                                            id="labelInstructions"
                                            aria-labelledby="Label instructions"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    color: "rgba(0, 0, 0, 0.5)",
                                                    fontStyle: "italic"
                                                }
                                            }}
                                            onChange={props.handleChange("labelInstructions")}
                                            onBlur={props.handleBlur("labelInstructions")}
                                            value={props.values.labelInstructions}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box marginBottom="15px">
                                            <Typography variant="h4">Other notes (optional)</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} className={classes.formItem}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            placeholder="Ex. substitutions, chewable options, flavor "
                                            name="notes"
                                            id="notes"
                                            aria-labelledby="notes"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    color: "rgba(0, 0, 0, 0.5)",
                                                    fontStyle: "italic"
                                                }
                                            }}
                                            onChange={props.handleChange("notes")}
                                            onBlur={props.handleBlur("notes")}
                                            value={props.values.notes}
                                        />
                                    </Grid>
                                    <Grid container item xs={12} className={classes.formItem}>
                                        <RadioGroup
                                            row
                                            name="giveWithFoodAndWater"
                                            id="giveWithFoodAndWater"
                                        >
                                            <Grid item xs="auto">
                                                <FormControlLabel
                                                    control={<Field
                                                        component={Checkbox}
                                                        // name="giveWithFoodAndWater"
                                                        color="secondary"
                                                        onChange={() => {
                                                            props.setFieldValue("giveWithFoodAndWater",
                                                                props.values.giveWithFoodAndWater === "true" ? "false" : "true");
                                                        }}
                                                        checked={props.values.giveWithFoodAndWater === "true"}
                                                    />}
                                                    label="Give with food and water"
                                                    labelPlacement="end"
                                                />
                                            </Grid>
                                        </RadioGroup>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box marginBottom="15px">
                                            <Typography variant="h4">Filling options</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid container item xs={12} className={classes.formItem}>
                                        <RadioGroup
                                            row
                                            name="fillingOption"
                                            id="fillingOption"
                                        >
                                            <Grid item xs="auto">
                                                <FormControlLabel
                                                    control={<Field
                                                        component={Radio}
                                                        // name="fillingOption"
                                                        color="secondary"
                                                        onChange={() => props.setFieldValue("fillingOption", "RALPHIE_PHARMACY")}
                                                        checked={props.values.fillingOption === "RALPHIE_PHARMACY"}
                                                    />}
                                                    label="The Video Vets pharmacy"
                                                    labelPlacement="end"
                                                />
                                            </Grid>
                                            <Grid item xs="auto">
                                                <FormControlLabel
                                                    value="Chat"
                                                    control={<Field
                                                        component={Radio}
                                                        // name="fillingOption"
                                                        color="secondary"
                                                        onChange={() => props.setFieldValue("fillingOption", "LOCAL_PHARMACY")}
                                                        checked={props.values.fillingOption === "LOCAL_PHARMACY"}
                                                    />}
                                                    label="Called into local (human) pharmacy"
                                                    labelPlacement="end"
                                                />
                                            </Grid>
                                        </RadioGroup>
                                        <ErrorMessage name="fillingOption">
                                            {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                        </ErrorMessage>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="15px">
                                        <Typography variant="h4">Potential Side Effects</Typography>
                                    </Box>
                                </Grid>
                                <Grid container item xs={12} className={classes.formItem}>
                                    <Box>
                                        {
                                            sideEffects.map(({label, value}, idx) => {
                                                return (
                                                    <Grid item xs="auto" key={idx}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedSideEffects[value]}
                                                                    onChange={(e) => handleSideEffectsChange(e)}
                                                                    name={value}
                                                                />
                                                            }
                                                            label={label}
                                                        />
                                                    </Grid>
                                                );
                                            }
                                            )
                                        }
                                    </Box>
                                    <ErrorMessage name="potentialSideEffects">
                                        {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                    </ErrorMessage>
                                </Grid>
                                <Grid container justify="flex-start" alignItems="center">
                                    <Grid item>
                                        <Button color="primary" variant="contained" type="submit"
                                            classes={{root: classes.button}}>
                                            Save Request
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button type="button" onClick={handleClose} variant="contained"
                                            classes={{root: classes.button}}>
                                            Cancel
                                        </Button>
                                    </Grid>
                                    {itemToEdit &&
                                    <Grid item>
                                        <Button type="button" onClick={() => {
                                            handleDelete();
                                            setOpenDialog(false);
                                        }} variant="contained"
                                        classes={{root: classes.deleteButton}}>
                                            Delete
                                        </Button>
                                    </Grid>
                                    }
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default PrescriptionModal;
